import React, { useCallback, useMemo, useState } from "react";
import { Modal, Button, ButtonGroup, DatePicker, Checkbox } from "@veneer/core";
import type { MomentObj } from "@veneer/core/dist/scripts/date_picker/date_picker";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  ICalendarEvent,
  IDeleteOccurrencePayload,
  ISelectableOccurrence,
  DeleteOccurrenceMode,
  buildSelectableOccurrences,
  isSelectableDate,
  isPastOccurrence,
  parseLocalDate,
} from "./agendaCalendarUtils";
import { IGetAppointmentOccurrenceByUser } from "../../models/dto/agendaApi/IGetAppointmentsByUser";

interface DeleteOccurrenceModalProps {
  show: boolean;
  mode: DeleteOccurrenceMode;
  event: ICalendarEvent | null;
  agenda: IGetAppointmentOccurrenceByUser | null;
  onClose: () => void;
  onPreparePayload: (payload: IDeleteOccurrencePayload) => void;
}

const getOccurrenceKey = (occ: ISelectableOccurrence, index: number): string => {
  const idPart = occ.occurrenceID !== null ? String(occ.occurrenceID) : String(index);
  return `${occ.dateStr}-${idPart}`;
};


const DeleteOnlyThisDate: React.FC<{
  event: ICalendarEvent;
  onConfirm: () => void;
  onClose: () => void;
}> = ({ event, onConfirm, onClose }) => {
  const [t] = useTranslation("global");
  const dateLabel = moment(event.start).format("ddd, LL");
  const isAlreadyPast = isPastOccurrence(event.start, event.end);

  return (
    <>
      <p className="deleteOccurrenceInfo">
        <strong>{t("header.selectedDate")}:</strong> {dateLabel}
      </p>
      <ButtonGroup className="createAgendaButtongrouop">
        <Button onClick={onConfirm} disabled={isAlreadyPast}>{t("header.submit")}</Button>
        <Button appearance="secondary" onClick={onClose}>
          {t("header.cancel")}
        </Button>
      </ButtonGroup>
    </>
  );
};


const DeleteByRange: React.FC<{
  agenda: IGetAppointmentOccurrenceByUser;
  onConfirm: (items: ISelectableOccurrence[]) => void;
  onClose: () => void;
}> = ({ agenda, onConfirm, onClose }) => {
  const [t] = useTranslation("global");
  const [rangeStart, setRangeStart] = useState<string>("");
  const [rangeEnd, setRangeEnd]     = useState<string>("");

  const allSelectable = useMemo(
    () => buildSelectableOccurrences(agenda),
    [agenda]
  );

  const isOutsideRange = useCallback(
    (day: MomentObj): boolean => !isSelectableDate(day.toDate(), agenda),
    [agenda]
  );

  const occurrencesInRange = useMemo((): ISelectableOccurrence[] => {
    if (!rangeStart || !rangeEnd) return [];
    const start = parseLocalDate(rangeStart);
    const end   = parseLocalDate(rangeEnd);
    return allSelectable.filter((occ) => {
      const d = parseLocalDate(occ.dateStr);
      return d >= start && d <= end;
    });
  }, [rangeStart, rangeEnd, allSelectable]);

  const handleDateChange = (date?: any) => {
    if (!date) return;
    if (date.startDate && typeof date.startDate.format === "function") {
      setRangeStart(date.startDate.format("YYYY-MM-DD"));
    }
    if (date.endDate && typeof date.endDate.format === "function") {
      setRangeEnd(date.endDate.format("YYYY-MM-DD"));
    }
  };

  return (
    <>
      <DatePicker
        className="createAgendaSelect createAgendaDatePicker"
        clearIcon={false}
        orientation="horizontal"
        range
        startDatePlaceholder="mm/dd/yyyy"
        endDatePlaceholder="mm/dd/yyyy"
        format="LL"
        startDate={rangeStart}
        endDate={rangeEnd}
        startDateLabel={t("header.stardDate")}
        endDateLabel={t("header.endDate")}
        isOutsideRange={isOutsideRange}
        onChange={handleDateChange}
      />

      {rangeStart && rangeEnd && (
        <div className="deleteOccurrenceRangeResults">
          <p className="deleteOccurrenceRangeLabel">{t("header.occurrencesInRange")}:</p>
          {occurrencesInRange.length === 0 ? (
            <p className="deleteOccurrenceNoItems">{t("header.noSelectableOccurrences")}</p>
          ) : (
            <ul className="deleteOccurrenceList">
              {occurrencesInRange.map((occ, index) => (
                <li key={getOccurrenceKey(occ, index)} className="deleteOccurrenceListItem">
                  {moment(parseLocalDate(occ.dateStr)).format("ddd, LL")}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <ButtonGroup className="createAgendaButtongrouop" style={{ marginTop: 16 }}>
        <Button
          onClick={() => onConfirm(occurrencesInRange)}
          disabled={occurrencesInRange.length === 0}
        >
          {t("header.submit")}
        </Button>
        <Button appearance="secondary" onClick={onClose}>
          {t("header.cancel")}
        </Button>
      </ButtonGroup>
    </>
  );
};


const DeleteSelectedDates: React.FC<{
  agenda: IGetAppointmentOccurrenceByUser;
  onConfirm: (items: ISelectableOccurrence[]) => void;
  onClose: () => void;
}> = ({ agenda, onConfirm, onClose }) => {
  const [t] = useTranslation("global");
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  const allSelectable = useMemo(
    () => buildSelectableOccurrences(agenda),
    [agenda]
  );

  const toggle = (dateStr: string) => {
    setSelectedKeys((prev) =>
      prev.includes(dateStr) ? prev.filter((k) => k !== dateStr) : [...prev, dateStr]
    );
  };

  const selectedItems = allSelectable.filter((occ) => selectedKeys.includes(occ.dateStr));

  return (
    <>
      {allSelectable.length === 0 ? (
        <p className="deleteOccurrenceNoItems">{t("header.noSelectableOccurrences")}</p>
      ) : (
        <div className="createAgendaDays" style={{ maxHeight: 220, overflowY: "auto", gridTemplateColumns: "1fr" }}>
          {allSelectable.map((occ, index) => (
            <Checkbox
              key={getOccurrenceKey(occ, index)}
              className="createAgendaCheckbox"
              label={moment(parseLocalDate(occ.dateStr)).format("ddd, LL")}
              checked={selectedKeys.includes(occ.dateStr)}
              onChange={() => toggle(occ.dateStr)}
            />
          ))}
        </div>
      )}

      <ButtonGroup className="createAgendaButtongrouop" style={{ marginTop: 16 }}>
        <Button
          onClick={() => onConfirm(selectedItems)}
          disabled={selectedItems.length === 0}
        >
          {t("header.submit")}
        </Button>
        <Button appearance="secondary" onClick={onClose}>
          {t("header.cancel")}
        </Button>
      </ButtonGroup>
    </>
  );
};


const MODE_TITLE_KEY: Record<DeleteOccurrenceMode, string> = {
  onlyThisDate:  "header.deleteOnlyThisDate",
  byRange:       "header.deleteByRange",
  selectedDates: "header.deleteSelectedDates",
};

const DeleteOccurrenceModal: React.FC<DeleteOccurrenceModalProps> = ({
  show,
  mode,
  event,
  agenda,
  onClose,
  onPreparePayload,
}) => {
  const [t] = useTranslation("global");

  if (!event || !agenda) return null;

  const buildPayload = (items: ISelectableOccurrence[]): IDeleteOccurrencePayload => ({
    appointmentID: agenda.appointmentID,
    occurrenceIDs: items
      .map((o) => o.occurrenceID)
      .filter((id): id is number => id !== null),
  });


  const handleConfirmOnlyThisDate = () => {
    const payload: IDeleteOccurrencePayload = {
      appointmentID: agenda.appointmentID,
      occurrenceIDs: event.occurrenceID !== null ? [event.occurrenceID] : [],
    };
    onPreparePayload(payload);
  };

  return (
    <div style={{ position: "relative" }}>
      <Modal
        align="center"
        closeButton
        id="modal-delete-occurrence-detail"
        show={show}
        onClose={onClose}
        title={t(MODE_TITLE_KEY[mode])}
      >
        <div className="deleteOccurrenceModalContent">
          {mode === "onlyThisDate" && (
            <DeleteOnlyThisDate
              event={event}
              onConfirm={handleConfirmOnlyThisDate}
              onClose={onClose}
            />
          )}

          {mode === "byRange" && (
            <DeleteByRange
              agenda={agenda}
              onConfirm={(items) => onPreparePayload(buildPayload(items))}
            onClose={onClose}
          />
        )}

          {mode === "selectedDates" && (
            <DeleteSelectedDates
              agenda={agenda}
              onConfirm={(items) => onPreparePayload(buildPayload(items))}
              onClose={onClose}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default DeleteOccurrenceModal;
